import { isValid } from 'date-fns';
import { observable, set } from 'mobx';
import { humanizeString, upperCaseEachWord } from './HumanizeString';
import { toTimezoneAndFormat } from './Date';
import { Duration, formatDurationExplicit } from './Duration';
import { Enums } from './Enums';

export class DisplayEventProfile implements Mri.DisplayEventProfiles.DisplayEventProfile {
    public static readableForm(
        profile: Mri.DisplayEventProfiles.DisplayEventProfile,
        alertAction?: Mri.DisplayAlerts.AlertAction,
        brief?: boolean
    ) {
        const setAction = DisplayEventProfile.readableActions.get(profile.displayEventStatus);

        const concatTypes = this.arrayOfEvents(profile);
        const activeInactive = profile.displayEventFilter.downtimeActiveIncident;
        const types = brief
            ? concatTypes.length > 3
                ? concatTypes.slice(0, 3).join(', ') + ` and ${concatTypes.length - 3} more`
                : concatTypes.join(', ')
            : concatTypes.join(', ');
        const start = profile.displayEventFilter.startTime;
        const end = profile.displayEventFilter.endTime;
        const min = profile.displayEventFilter.minDuration ?? 0;
        const max = profile.displayEventFilter.maxDuration ?? 0;
        const messages: string[] = [];
        
        if (!(alertAction || setAction) || !types) return '';

        if (setAction) {
            messages.push(setAction);
        }
        if (alertAction) {
            messages.push(`Alerts ${alertAction.name} of`);
        }
        if (types) {
            let activeInactiveString = '';
            if(activeInactive == null) {
                activeInactiveString = ', Active and Inactive';
            } else {
                activeInactiveString = activeInactive ? ', Active' : ', Inactive';
            }
            messages.push(types + activeInactiveString + ' events');
        }
        if (start && end && isValid(start) && isValid(end)) {
            messages.push(
                `between ${toTimezoneAndFormat(start, 'hh:mm a', 'UTC')} and ${toTimezoneAndFormat(
                    end,
                    'hh:mm a',
                    'UTC'
                )}`
            );
        }
        if (min === undefined || max === undefined) {
            messages.push('Please set a valid start and end time.');
        } else if (min && max) {
            messages.push(
                `for a duration of ${formatDurationExplicit(min as Duration)} to ${formatDurationExplicit(
                    max as Duration
                )}`
            );
        } else if (max) {
            messages.push(`up to a duration of ${formatDurationExplicit(max as Duration)}`);
        } else if (min) {
            messages.push(`with at least a duration of ${formatDurationExplicit(min as Duration)}`);
        }

        if (profile.displayEventExpiration) {
            messages.push(
                `with Events/Alerts expiring in ${formatDurationExplicit(profile.displayEventExpiration as Duration)}`
            );
        }

        return messages.join(' ') + '.';
    }

    public static arrayOfEvents(profile: Mri.DisplayEventProfiles.DisplayEventProfile) {
        const des = (profile.displayEventFilter.displayEventTypes ?? []).map(v => {
            const s = Enums.PortalDisplayEventType.has(v) ? Enums.PortalDisplayEventType : Enums.DisplayEventType;
            return upperCaseEachWord(humanizeString(Enums.toString(s, v)));
        });
        const dtis = (profile.displayEventFilter.downtimeIncidentTypes ?? []).map(v => {
            const s = Enums.PortalDowntimeIncidentType.has(v)
                ? Enums.PortalDowntimeIncidentType
                : Enums.DowntimeIncidentType;
            return upperCaseEachWord(humanizeString(Enums.toString(s, v).replace('INCIDENT_', '')));
        });
        const concatTypes = des.concat(dtis);

        return concatTypes;
    }

    private static readableActions = new Map<Mri.DisplayEvents.DisplayEventStatus | undefined, string>([
        [undefined, 'Unknown'],
        [Mri.DisplayEvents.DisplayEventStatus.New, 'No Action'],
        [Mri.DisplayEvents.DisplayEventStatus.Alert, 'Escalates to Alert'],
        [Mri.DisplayEvents.DisplayEventStatus.Ticket, 'Flags as handled by Ticket'],
        [Mri.DisplayEvents.DisplayEventStatus.Resolved, 'Resolves'],
        [Mri.DisplayEvents.DisplayEventStatus.Closed, 'Closes'],
        [Mri.DisplayEvents.DisplayEventStatus.Ignored, 'Ignores'],
        [Mri.DisplayEvents.DisplayEventStatus.Delayed, 'Delays'],
        [Mri.DisplayEvents.DisplayEventStatus.Suppressed, 'Suppresses'],
    ]);
    public constructor(model: Mri.DisplayEventProfiles.DisplayEventProfile) {
        set(this, model);
    }

    public readableForm(alertAction?: Mri.DisplayAlerts.AlertAction) {
        return DisplayEventProfile.readableForm(this, alertAction);
    }

    @observable public account?: string;
    @observable public alertActionGuid?: string;
    @observable public alertPeriod?: Duration;
    @observable public displayEventExpiration!: Duration;
    @observable public displayEventStatus!: Mri.DisplayEvents.DisplayEventStatus;
    @observable public incidentLevel?: Mri.DisplayApi.IncidentLevel;
    @observable public displayEventProfileGuid!: string;
    @observable public name!: string;
    @observable public comment!: string;
    @observable public displayEventFilter!: Mri.DisplayEvents.DisplayEventFilter;
    @observable public displayFilter!: Mri.DisplayFilters.DisplayFilter;
    @observable public alertingProfile!: boolean;
    @observable public automationProfile!: boolean;

    @observable public displayEventIncidents?: string[];
}
